/**
 * SupervisorDashboard — Operations overview for supervisors.
 *
 * Shows facility-wide task stats, team status, notifications, and
 * a composite PulseIndicator. All data comes from the same generic
 * queries used by AdminDashboard — no domain-specific queries.
 *
 * DRR-055: Dumb Frontend — renders backend data, zero domain logic.
 */

import { useMemo, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  SimpleGrid,
  Card,
  Badge,
  Button,
  ThemeIcon,
  Loader,
  Alert,
  Table,
  Tabs,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconRefresh,
  IconUsers,
  IconListCheck,
  IconClock,
  IconCheck,
  IconBell,
  IconChartBar,
} from '@tabler/icons-react';
import { useTenantId, useAuth } from '@/auth';
import {
  useListTasksQuery,
  useListActorsQuery,
  useListNotificationsForRecipientQuery,
  useGetTaskStatisticsQuery,
} from '@/generated/graphql';
import { normalizeMany } from '@/normalizers';
import { UnifiedStatus } from '@/types/normalized-entity';
import { SafetyBadge, StatusBadge } from '@/components/SafetyBadge';
import { PulseIndicator } from '@/components/PulseIndicator';

// ============================================================================
// COMPONENT
// ============================================================================

export function SupervisorDashboard() {
  const tenantId = useTenantId() ?? '';
  const { user } = useAuth();
  const skip = !tenantId;

  // Backend queries — same generic hooks as AdminDashboard
  const { data: taskStatsData, loading: taskStatsLoading, refetch: refetchTaskStats } = useGetTaskStatisticsQuery({ skip });
  const { data: tasksData, loading: tasksLoading, refetch: refetchTasks } = useListTasksQuery({
    variables: { limit: 100 },
    skip,
  });
  const { data: actorsData, loading: actorsLoading, refetch: refetchActors } = useListActorsQuery({
    variables: { limit: 100 },
    skip,
  });
  const { data: notificationsData, loading: notificationsLoading, refetch: refetchNotifications } = useListNotificationsForRecipientQuery({
    variables: { recipientActorId: user?.id ?? '', limit: 50 },
    skip: skip || !user?.id,
  });

  // Normalize for type-safe access
  const normalizedTasks = useMemo(
    () => normalizeMany(tasksData?.taskApiListTasks?.items ?? []),
    [tasksData],
  );
  const normalizedActors = useMemo(
    () => normalizeMany(actorsData?.listActors?.items ?? []),
    [actorsData],
  );
  const normalizedNotifications = useMemo(
    () => normalizeMany(notificationsData?.listNotificationsForRecipient?.items ?? []),
    [notificationsData],
  );

  // Computed stats
  const stats = useMemo(() => {
    const taskStats = taskStatsData?.taskApiGetTaskStatistics;
    const activeActors = normalizedActors.filter(a => a.unifiedStatus === UnifiedStatus.ACTIVE).length;
    const pendingNotifications = normalizedNotifications.filter(n => n.unifiedStatus === UnifiedStatus.PENDING).length;

    return {
      pendingTasks: taskStats?.pendingCount ?? 0,
      inProgressTasks: taskStats?.inProgressCount ?? 0,
      completedTasks: taskStats?.completedCount ?? 0,
      slaBreached: taskStats?.slaBreachedCount ?? 0,
      activeActors,
      totalActors: normalizedActors.length,
      pendingNotifications,
    };
  }, [taskStatsData, normalizedActors, normalizedNotifications]);

  // Tasks by status
  const overdueTasks = useMemo(
    () => normalizedTasks.filter(t => t.unifiedStatus === UnifiedStatus.FAILED),
    [normalizedTasks],
  );
  const activeTasks = useMemo(
    () => normalizedTasks.filter(t => t.unifiedStatus === UnifiedStatus.ACTIVE || t.unifiedStatus === UnifiedStatus.PENDING),
    [normalizedTasks],
  );

  const loading = taskStatsLoading || tasksLoading || actorsLoading || notificationsLoading;

  const handleRefresh = useCallback(() => {
    refetchTaskStats();
    refetchTasks();
    refetchActors();
    refetchNotifications();
  }, [refetchTaskStats, refetchTasks, refetchActors, refetchNotifications]);

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view the dashboard.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header with pulse indicator */}
        <Group justify="space-between">
          <Group gap="md">
            <div>
              <Title order={2}>Operations</Title>
              <Text c="dimmed" size="sm">
                {user?.displayName ? `${user.displayName} — ` : ''}Overview
              </Text>
            </div>
            {/* Backend-computed PulseResult will be passed when API exists */}
            <PulseIndicator result={null} />
          </Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={handleRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>

        {/* SLA breach alert */}
        {stats.slaBreached > 0 && (
          <Alert color="red" title="Overdue Tasks" icon={<IconAlertTriangle />}>
            {stats.slaBreached} task{stats.slaBreached !== 1 ? 's are' : ' is'} past SLA deadline.
          </Alert>
        )}

        {/* Stats — all generic, no domain field names */}
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <Card withBorder padding="md">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Active Tasks</Text>
              <ThemeIcon variant="light" size="sm" color="blue">
                <IconListCheck size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{stats.inProgressTasks}</Text>
            <Text size="xs" c="dimmed">{stats.pendingTasks} pending</Text>
          </Card>

          <Card withBorder padding="md">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Completed</Text>
              <ThemeIcon variant="light" size="sm" color="green">
                <IconCheck size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{stats.completedTasks}</Text>
          </Card>

          <Card withBorder padding="md">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Team Active</Text>
              <ThemeIcon variant="light" size="sm" color="cyan">
                <IconUsers size={14} />
              </ThemeIcon>
            </Group>
            <Group gap="xs">
              <Text size="xl" fw={700}>{stats.activeActors}</Text>
              <Text size="sm" c="dimmed">/ {stats.totalActors}</Text>
            </Group>
          </Card>

          <Card withBorder padding="md">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Overdue</Text>
              <ThemeIcon variant="light" size="sm" color={stats.slaBreached > 0 ? 'red' : 'gray'}>
                <IconClock size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c={stats.slaBreached > 0 ? 'red' : undefined}>{stats.slaBreached}</Text>
          </Card>
        </SimpleGrid>

        {/* Main content */}
        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <Tabs defaultValue="tasks">
            <Tabs.List>
              <Tabs.Tab value="tasks" leftSection={<IconListCheck size={14} />}>
                Tasks ({activeTasks.length})
              </Tabs.Tab>
              <Tabs.Tab value="team" leftSection={<IconUsers size={14} />}>
                Team ({stats.totalActors})
              </Tabs.Tab>
              <Tabs.Tab
                value="alerts"
                leftSection={<IconBell size={14} />}
                rightSection={
                  stats.pendingNotifications > 0 ? (
                    <Badge size="xs" variant="filled" color="red">
                      {stats.pendingNotifications}
                    </Badge>
                  ) : undefined
                }
              >
                Alerts
              </Tabs.Tab>
              <Tabs.Tab value="overdue" leftSection={<IconAlertTriangle size={14} />}>
                Overdue ({overdueTasks.length})
              </Tabs.Tab>
            </Tabs.List>

            {/* Tasks tab */}
            <Tabs.Panel value="tasks" pt="md">
              <Paper withBorder>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Assigned To</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Safety</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {activeTasks.slice(0, 20).map((task) => (
                      <Table.Tr key={task.id}>
                        <Table.Td>
                          <Text fw={500} size="sm">{task.displayName}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm">{task.domainData.taskType}</Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">{task.domainData.assignedActor?.displayName ?? 'Unassigned'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <StatusBadge status={task.unifiedStatus} size="sm" />
                        </Table.Td>
                        <Table.Td>
                          <SafetyBadge entity={task} size="xs" />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    {activeTasks.length === 0 && (
                      <Table.Tr>
                        <Table.Td colSpan={5}>
                          <Text ta="center" c="dimmed" py="md">No active tasks</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </Paper>
            </Tabs.Panel>

            {/* Team tab */}
            <Tabs.Panel value="team" pt="md">
              <Paper withBorder>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Category</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Capacity</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {normalizedActors.slice(0, 20).map((actor) => (
                      <Table.Tr key={actor.id}>
                        <Table.Td>
                          <Text fw={500} size="sm">{actor.displayName}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm">{actor.domainData.actorType}</Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{actor.domainData.category ?? '-'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <StatusBadge status={actor.unifiedStatus} size="sm" />
                            {actor.domainData.circuitBreakerOpen && (
                              <Badge color="red" size="xs">Circuit Open</Badge>
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{actor.domainData.maxCapacity ?? '-'}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    {normalizedActors.length === 0 && (
                      <Table.Tr>
                        <Table.Td colSpan={5}>
                          <Text ta="center" c="dimmed" py="md">No team members found</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </Paper>
            </Tabs.Panel>

            {/* Alerts tab */}
            <Tabs.Panel value="alerts" pt="md">
              <Stack gap="xs">
                {normalizedNotifications.length === 0 ? (
                  <Paper p="xl" withBorder ta="center">
                    <ThemeIcon size="xl" variant="light" color="gray" mb="md">
                      <IconBell size={24} />
                    </ThemeIcon>
                    <Text fw={500}>No alerts</Text>
                    <Text size="sm" c="dimmed">Notifications will appear here.</Text>
                  </Paper>
                ) : (
                  normalizedNotifications.slice(0, 20).map((notif) => (
                    <Paper
                      key={notif.id}
                      p="sm"
                      withBorder
                      style={{
                        backgroundColor: notif.unifiedStatus === UnifiedStatus.PENDING ? 'var(--mantine-color-blue-light)' : undefined,
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                          <ThemeIcon variant="light" size="sm">
                            <IconBell size={12} />
                          </ThemeIcon>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={notif.unifiedStatus === UnifiedStatus.PENDING ? 500 : 400} lineClamp={1}>
                              {notif.displayName}
                            </Text>
                            <Text size="xs" c="dimmed" lineClamp={2}>{notif.domainData.messageTemplate}</Text>
                            <Text size="xs" c="dimmed" mt={2}>{new Date(notif.domainData.createdAt).toLocaleString()}</Text>
                          </div>
                        </Group>
                        <Group gap={4}>
                          <StatusBadge status={notif.unifiedStatus} size="xs" />
                          <Badge size="xs" color={notif.domainData.priority === 'HIGH' ? 'red' : 'gray'}>
                            {notif.domainData.priority}
                          </Badge>
                        </Group>
                      </Group>
                    </Paper>
                  ))
                )}
              </Stack>
            </Tabs.Panel>

            {/* Overdue tab */}
            <Tabs.Panel value="overdue" pt="md">
              <Paper withBorder>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Assigned To</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Safety</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {overdueTasks.map((task) => (
                      <Table.Tr key={task.id}>
                        <Table.Td>
                          <Text fw={500} size="sm" c="red">{task.displayName}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm">{task.domainData.taskType}</Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">{task.domainData.assignedActor?.displayName ?? 'Unassigned'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <StatusBadge status={task.unifiedStatus} size="sm" />
                        </Table.Td>
                        <Table.Td>
                          <SafetyBadge entity={task} size="xs" />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    {overdueTasks.length === 0 && (
                      <Table.Tr>
                        <Table.Td colSpan={5}>
                          <Text ta="center" c="dimmed" py="md">No overdue tasks</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </Paper>
            </Tabs.Panel>
          </Tabs>
        )}
      </Stack>
    </Container>
  );
}

export default SupervisorDashboard;
